/**
 * หน้าจอ Supplier Master (P2-1b) — รายการผู้ขาย ค้นหา สร้าง และแก้ไข
 *
 * ปิดสามในสี่เส้นทางของ contract ที่ `WO-P2-M1-001` เปิดไว้ (D-083):
 * `GET /v1/suppliers`, `POST /v1/suppliers` และ `PATCH /v1/suppliers/{supplier_id}`
 *
 * โครงของหน้าจอยืมมาจาก `system/DepartmentsScreen` ทั้งท่อนโดยตั้งใจ — ช่องค้นหา
 * อยู่บนสุด ฟอร์มสร้างอยู่หลังปุ่มในกล่อง `Modal` และหลังบันทึกสำเร็จก็ **โหลด
 * รายการใหม่ผ่าน client เส้นเดิม** ไม่ใช่รีโหลดทั้งหน้า (ไม่มี
 * `window.location.reload` ที่ไหนในไฟล์นี้) สิ่งที่หน้านี้มีเพิ่มคือ **การแก้ไข
 * รายแถว** ซึ่งแผนกไม่มี เพราะ contract ของแผนกไม่มี `PATCH`
 *
 * สองข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:
 *
 * 1. **`supplier_code` แก้ไม่ได้** (D-083) กล่องแก้ไขจึงแสดงรหัสเป็นข้อความอ่าน
 *    อย่างเดียว ไม่ใช่ช่องกรอกที่ disabled ไว้เฉย ๆ — ใบสั่งซื้อ การตรวจรับ และ lot
 *    ที่อ้างรหัสนั้นอยู่ต้องตามสอบกลับมาถึงผู้ขายรายนี้ได้เสมอ และคำขอที่มีคีย์นั้น
 *    ติดไปด้วยจะถูกตอบ `400 SUPPLIER_CODE_IS_IMMUTABLE`
 * 2. **ไม่มีการลบผู้ขาย** เลิกใช้ผู้ขายคือการเปลี่ยนสถานะเป็น `INACTIVE` ซึ่งเป็น
 *    สิ่งที่กล่องแก้ไขทำ ไม่ใช่ปุ่มที่หายไปเพราะยังไม่ได้ทำ
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับทุกหน้าจอธุรกิจ: `SUPPLIER_MASTER` + `VIEW` เป็น
 * สิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `CREATE` และ `EDIT` เป็น
 * สิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม — และทั้งสองแยกจากกัน
 * จริง คนที่เพิ่มผู้ขายใหม่ได้ไม่จำเป็นต้องแก้ของเดิมได้ และกลับกัน
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5) ที่ซ่อนปุ่มเพราะไม่อยาก
 * ให้ผู้ใช้กรอกฟอร์มแล้วโดนปฏิเสธตอนกดบันทึก
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createSupplier,
  fetchSupplier,
  fetchSuppliers,
  ruleFromFailure,
  updateSupplier,
  type ApiErrorEnvelope,
  type SupplierFailure,
} from '../../lib/suppliers/client';
import {
  SUPPLIER_STATUS_DESCRIPTORS,
  filterSuppliers,
  offeredSupplierStatuses,
  supplierStatusLabel,
  supplierStatusTone,
  toCreateSupplierBody,
  toUpdateSupplierBody,
} from '../../lib/suppliers/suppliersView';
import {
  SUPPLIER_CODE_IS_IMMUTABLE,
  SUPPLIER_CODE_MAX_LENGTH,
  SUPPLIER_CODE_TAKEN,
  SUPPLIER_FIELD_IS_SERVER_OWNED,
  SUPPLIER_MASTER_MODULE,
  SUPPLIER_NAME_MAX_LENGTH,
  type Supplier,
} from '../../lib/suppliers/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type SupplierFailureContext =
  /** อ่านรายการผู้ขาย */
  | 'suppliers'
  /** สร้างผู้ขายใหม่ */
  | 'create'
  /** แก้ไขผู้ขายรายเดิม */
  | 'edit';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ
 * context ที่อ่านอย่างเดียว
 *
 * ทุก write ของกลุ่มนี้เขียน `audit_log` (entity_type `Supplier`) **ในธุรกรรม
 * เดียวกับการเปลี่ยนข้อมูล** ล้มจุดใดก็ไม่เหลือทั้งสองอย่าง — ประโยคนี้จึงเป็น
 * ข้อเท็จจริงของ contract ไม่ใช่คำปลอบใจ
 */
function nothingChangedText(context: SupplierFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีผู้ขายรายใดถูกสร้างและไม่มีแถว audit ถูกเขียน';
    case 'edit':
      return ' ข้อมูลผู้ขายรายนี้ยังเป็นชุดเดิมทุกฟิลด์และไม่มีแถว audit ถูกเขียน';
    case 'suppliers':
      return '';
  }
}

function isWriteContext(context: SupplierFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case SUPPLIER_CODE_TAKEN:
      return {
        heading: 'รหัสผู้ขายนี้มีเจ้าของอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${SUPPLIER_CODE_TAKEN} — รหัสผู้ขายต้องไม่ซ้ำกันทั้งองค์กรและแก้ไม่ได้หลังสร้าง (D-083) ใช้รหัสอื่น หรือถ้าผู้ขายที่ตั้งใจจะเพิ่มคือรายที่ถือรหัสนี้อยู่แล้ว เขามีอยู่ในระบบแล้วและแก้ไขได้จากรายการด้านล่าง **ไม่มีผู้ขายรายใดถูกสร้างขึ้น และข้อมูลของรายที่ถือรหัสนี้ไม่ถูกทับ**`,
      };
    case SUPPLIER_CODE_IS_IMMUTABLE:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — รหัสผู้ขายถูกส่งไปกับคำขอแก้ไข',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${SUPPLIER_CODE_IS_IMMUTABLE} — รหัสผู้ขายเป็น business key ที่ใบสั่งซื้อ การตรวจรับ และ lot อ้างอยู่ จึงเขียนครั้งเดียวตอนสร้างและแก้ไม่ได้อีกเลย กล่องแก้ไขนี้ไม่มีเส้นทางที่ส่งรหัสไปกับคำขอด้วยซ้ำ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ อย่าลองกดซ้ำ **ข้อมูลผู้ขายรายนี้ยังเป็นชุดเดิมทุกฟิลด์**`,
      };
    case SUPPLIER_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${SUPPLIER_FIELD_IS_SERVER_OWNED} — รหัสภายใน (supplier_id) และเวลาที่สร้าง/แก้ไขเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: SupplierFailure,
  context: SupplierFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
        text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${SUPPLIER_MASTER_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ ข้อมูลผู้ขายเป็นข้อมูลระดับองค์กรและไม่มีขอบเขตโรงงาน (D-083) การถูกปฏิเสธจึงไม่ใช่เรื่องของโรงงานที่คุณสังกัด — ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
      };
    case 'not-found':
      return {
        heading: 'ไม่พบผู้ขายรายที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ผู้ขายรายนี้ไม่มีอยู่แล้ว (อาจถูกแก้โดยผู้ใช้อีกคนระหว่างที่หน้าจอนี้เปิดค้างอยู่) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — รหัสที่ส่งไปมีเจ้าของอยู่แล้ว ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น รหัสยาวเกิน ${SUPPLIER_CODE_MAX_LENGTH} ตัวอักษร ชื่อยาวเกิน ${SUPPLIER_NAME_MAX_LENGTH} ตัวอักษร หรือสถานะที่ไม่อยู่ในสามค่าที่รับได้)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรายการผู้ขายที่อ่านได้ไม่ครบ — รายการที่ขาดไปหนึ่งรายอ่านเหมือน "ยังไม่มีผู้ขายรายนี้" ซึ่งจะพาไปสู่การสร้างรหัสที่มีเจ้าของอยู่แล้วซ้ำเข้าไป',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: SupplierFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

interface SupplierFailureNoticeProps {
  readonly failure: SupplierFailure;
  readonly context: SupplierFailureContext;
  readonly onRetry?: () => void;
}

export function SupplierFailureNotice({
  failure,
  context,
  onRetry,
}: SupplierFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ใช้แก้เองได้** (ใช้รหัสอื่น) ต่างจากความ
  // ล้มเหลวที่เหลือซึ่งเขาทำอะไรไม่ได้เลย — สีของป้ายจึงบอกเรื่องนั้น
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="supplier-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="supplier-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="supplier-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface SupplierScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type SuppliersState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Supplier[] }
  | SupplierFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type PendingKind = 'create-supplier' | 'update-supplier';

type LinkedSupplierState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly supplier: Supplier }
  | SupplierFailure;

interface LinkedSupplierProps {
  readonly supplierId: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl: typeof fetch | undefined;
  readonly onClose: () => void;
}

/** ผู้ขายรายเดียวจาก `?supplier_id=` — หาไม่เจอแสดงข้อความ ไม่กระทบรายการด้านล่าง */
function LinkedSupplier({ supplierId, apiBaseUrl, fetchImpl, onClose }: LinkedSupplierProps) {
  const [state, setState] = useState<LinkedSupplierState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });
    void fetchSupplier(supplierId, { apiBaseUrl, fetchImpl, signal: controller.signal }).then(
      (result) => {
        if (active && result.status !== 'cancelled') {
          setState(result);
        }
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [supplierId, apiBaseUrl, fetchImpl]);

  if (state.status === 'loading') {
    return (
      <p className="cap-notice__text" role="status" data-testid="supplier-linked-loading">
        กำลังโหลดผู้ขาย…
      </p>
    );
  }
  if (state.status !== 'ready') {
    return (
      <div data-testid="supplier-linked-error">
        {state.status === 'not-found' ? (
          <p className="cap-notice" role="alert">
            ไม่พบผู้ขายนี้ หรือไม่มีสิทธิ์เห็นผู้ขายรายนี้
          </p>
        ) : (
          <SupplierFailureNotice failure={state} context="suppliers" />
        )}
        <button type="button" className="cap-button cap-button--ghost" onClick={onClose}>
          ปิด
        </button>
      </div>
    );
  }
  const { supplier } = state;
  return (
    <section className="cap-card" data-testid="supplier-linked" aria-label="ผู้ขายที่เลือก">
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          <code className="cap-table__code">{supplier.supplier_code}</code> {supplier.supplier_name}
        </h2>
        <StatusBadge tone={supplierStatusTone(supplier.status)}>
          {supplierStatusLabel(supplier.status)}
        </StatusBadge>
      </div>
      <button type="button" className="cap-button cap-button--ghost" onClick={onClose}>
        ปิด
      </button>
    </section>
  );
}

export function SupplierScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: SupplierScreenProps) {
  const canCreate = can(permissions, SUPPLIER_MASTER_MODULE, 'CREATE');
  const canEdit = can(permissions, SUPPLIER_MASTER_MODULE, 'EDIT');
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSupplierId = searchParams.get('supplier_id')?.trim() ?? '';

  const [suppliers, setSuppliers] = useState<SuppliersState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingKind | null>(null);

  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createCode, setCreateCode] = useState('');
  const [createName, setCreateName] = useState('');
  const [createStatus, setCreateStatus] = useState('');
  const [createFailure, setCreateFailure] = useState<SupplierFailure | null>(null);
  const [createdSupplier, setCreatedSupplier] = useState<Supplier | null>(null);

  /** ผู้ขายที่กล่องแก้ไขกำลังเปิดค้างอยู่ — `null` คือไม่มีกล่องเปิด */
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editFailure, setEditFailure] = useState<SupplierFailure | null>(null);
  const [updatedSupplier, setUpdatedSupplier] = useState<Supplier | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังบันทึกสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ใช้เพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setSuppliers((previous) =>
      previous.status === 'ready' ? previous : { status: 'loading' },
    );

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchSuppliers({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setSuppliers(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setCreateCode('');
    setCreateName('');
    setCreateStatus('');
    setCreateFailure(null);
    setCreatedSupplier(null);
    setCreateOpen(true);
  };

  /**
   * อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` ถ้าฟังก์ชันเปลี่ยน
   * ตัวทุกครั้งที่ผู้ใช้พิมพ์ listener จะถูกผูกใหม่ทุกตัวอักษรโดยไม่จำเป็น
   */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const openEdit = (supplier: Supplier) => {
    if (!canEdit) {
      return;
    }
    // ค่าเริ่มต้นของฟอร์มคือ **ค่าที่เซิร์ฟเวอร์ส่งมาล่าสุด** ไม่ใช่ค่าว่าง — กล่องนี้
    // แก้ของที่มีอยู่ ไม่ได้สร้างใหม่ และ `toUpdateSupplierBody` จะไม่ส่งฟิลด์ที่
    // ผู้ใช้ไม่ได้แตะไปกับคำขอ
    setEditName(supplier.supplier_name);
    setEditStatus(supplier.status);
    setEditFailure(null);
    setUpdatedSupplier(null);
    setEditing(supplier);
  };

  const closeEdit = useCallback(() => {
    setEditing(null);
  }, []);

  const submitCreate = async () => {
    const body = toCreateSupplierBody(createCode, createName, createStatus);
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending('create-supplier');
    setCreateFailure(null);
    setCreatedSupplier(null);

    const result = await createSupplier(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่ผู้ใช้เห็นยังเป็นสถานะที่
      // เซิร์ฟเวอร์ยืนยันล่าสุด และสิ่งที่เขาพิมพ์ไว้ยังอยู่ในช่องให้แก้แล้วส่งใหม่
      // ได้ทันที ซึ่งสำหรับ `SUPPLIER_CODE_TAKEN` คือสิ่งเดียวที่เขาต้องทำ
      setCreateFailure(result);
      return;
    }

    setCreatedSupplier(result.supplier);
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นผู้ขายที่เพิ่งสร้าง (ซึ่งมักไม่ตรงกับคำค้นเดิม)
    // จะถูกกรองออกทันทีที่โหลดเสร็จ และผู้ใช้จะเห็นคำว่า "สร้างแล้ว" คู่กับรายการที่
    // ไม่มีผู้ขายรายนั้นอยู่เลย
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  const submitEdit = async () => {
    const target = editing;
    if (target === null || pending !== null || !canEdit) {
      return;
    }
    const body = toUpdateSupplierBody(target, editName, editStatus);
    if (body === null) {
      return;
    }

    setPending('update-supplier');
    setEditFailure(null);
    setUpdatedSupplier(null);

    const result = await updateSupplier(target.supplier_id, body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setEditFailure(result);
      return;
    }

    setUpdatedSupplier(result.supplier);
    setEditing(null);
    // คำค้นไม่ถูกล้างที่นี่ ต่างจากการสร้าง: แถวที่เพิ่งแก้มาจากรายการที่ผู้ใช้กำลัง
    // มองอยู่ การล้างคำค้นจะพาเขาออกจากตำแหน่งที่เขาทำงานอยู่โดยไม่ได้ขอ
    setReloadToken((token) => token + 1);
  };

  const supplierItems = suppliers.status === 'ready' ? suppliers.items : [];
  const matchedItems = filterSuppliers(supplierItems, query);
  const busy = pending !== null;
  const editBody =
    editing === null ? null : toUpdateSupplierBody(editing, editName, editStatus);

  return (
    <div className="cap-page" data-testid="supplier-master">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ผู้ขาย (Supplier Master)
            <InfoHint
              label="เกี่ยวกับทะเบียนผู้ขาย"
              text={
                <>
                  ทะเบียนผู้ขายของทั้งองค์กร — <strong>ไม่แยกตามโรงงาน</strong> (D-083)
                  ใบสั่งซื้อ การตรวจรับ และ lot ที่รับเข้ามาอ้างถึงผู้ขายจากที่นี่
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canCreate && !canEdit ? (
        <div className="cap-notice" data-testid="supplier-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">
            สิทธิ์ชุดนี้ยังแก้ทะเบียนผู้ขายไม่ได้
            <InfoHint
              label="เกี่ยวกับการซ่อนปุ่มที่นี่"
              text="การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ"
            />
          </h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag{' '}
            <code className="cap-table__code">can_create</code> หรือ{' '}
            <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{SUPPLIER_MASTER_MODULE}</code> ปุ่ม
            &quot;เพิ่มผู้ขาย&quot; และ &quot;แก้ไข&quot; จึงไม่แสดง — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
        </div>
      ) : null}

      {linkedSupplierId !== '' ? (
        <LinkedSupplier
          supplierId={linkedSupplierId}
          apiBaseUrl={apiBaseUrl}
          fetchImpl={fetchImpl}
          onClose={() => {
            setSearchParams(
              (current) => {
                const next = new URLSearchParams(current);
                next.delete('supplier_id');
                return next;
              },
              { replace: true },
            );
          }}
        />
      ) : null}

      {/* ------------------------------------------------- ค้นหา + ปุ่มสร้าง -- */}

      <div className="cap-filters" data-testid="supplier-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหาผู้ขาย</span>
          <input
            className="cap-input"
            type="search"
            name="supplier_query"
            data-testid="supplier-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <InfoHint
            label="เกี่ยวกับการค้นหาผู้ขาย"
            text="กรองรายการด้านล่างจากรหัสหรือชื่อผู้ขาย — กรองจากรายการที่โหลดมาแล้ว ไม่ได้ถามเซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกราย"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="supplier-create-open"
            onClick={openCreate}
          >
            เพิ่มผู้ขาย
          </button>
        ) : null}
      </div>

      {/* --------------------------------------------------------- สร้างใหม่ -- */}

      <Modal open={canCreate && createOpen} onClose={closeCreate} title="เพิ่มผู้ขายใหม่">
        <form
          className="cap-form"
          data-testid="supplier-create-form"
          aria-label="เพิ่มผู้ขายใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสผู้ขาย</span>
            <input
              className="cap-input"
              name="supplier_code"
              data-testid="supplier-create-code"
              value={createCode}
              maxLength={SUPPLIER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateCode(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสผู้ขาย"
              text={
                <>
                  ยาวไม่เกิน {SUPPLIER_CODE_MAX_LENGTH} ตัวอักษร ต้องไม่ซ้ำกันทั้งองค์กร และ{' '}
                  <strong>แก้ไม่ได้อีกเลยหลังสร้าง</strong> — ใบสั่งซื้อและการตรวจรับอ้างรหัสนี้
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ชื่อผู้ขาย</span>
            <input
              className="cap-input"
              name="supplier_name"
              data-testid="supplier-create-name"
              value={createName}
              maxLength={SUPPLIER_NAME_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateName(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับชื่อผู้ขาย"
              text={`ยาวไม่เกิน ${SUPPLIER_NAME_MAX_LENGTH} ตัวอักษร — แก้ทีหลังได้`}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">สถานะ</span>
            <select
              className="cap-input"
              name="status"
              data-testid="supplier-create-status"
              value={createStatus}
              onChange={(event) => {
                setCreateStatus(event.target.value);
              }}
            >
              {/* ค่าว่าง = ไม่ส่งคีย์นี้ไปเลย ไม่ใช่ส่งสตริงว่าง — เซิร์ฟเวอร์เติม
                  `ACTIVE` ให้ตาม contract */}
              <option value="">ใช้ค่าเริ่มต้นของเซิร์ฟเวอร์ (ACTIVE)</option>
              {SUPPLIER_STATUS_DESCRIPTORS.map((descriptor) => (
                <option key={descriptor.code} value={descriptor.code}>
                  {descriptor.label}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับสถานะผู้ขายที่เลือกได้ตอนสร้าง"
              text={SUPPLIER_STATUS_DESCRIPTORS.map((descriptor) => descriptor.summary).join(
                ' / ',
              )}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="supplier-create-submit"
              disabled={busy || toCreateSupplierBody(createCode, createName, createStatus) === null}
            >
              {pending === 'create-supplier' ? 'กำลังเพิ่มผู้ขาย…' : 'เพิ่มผู้ขาย'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="supplier-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — ผู้ใช้แก้รหัสแล้ว
           * กดส่งใหม่ได้ทันทีโดยไม่ต้องเปิดกล่องใหม่และพิมพ์ใหม่ทั้งหมด
           */}
          {createFailure === null ? null : (
            <SupplierFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdSupplier === null ? null : (
        <div className="cap-notice" data-testid="supplier-create-result" role="status">
          <StatusBadge tone="success">เพิ่มแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            เพิ่มผู้ขาย {createdSupplier.supplier_name} แล้ว
          </h2>
          <p className="cap-notice__text">
            รหัส <code className="cap-table__code">{createdSupplier.supplier_code}</code>{' '}
            สถานะ {supplierStatusLabel(createdSupplier.status)} —{' '}
            <strong>รหัสนี้แก้ไม่ได้อีกแล้ว</strong> ส่วนชื่อและสถานะแก้ได้จากปุ่ม
            &quot;แก้ไข&quot; ในรายการด้านล่าง
          </p>
        </div>
      )}

      {/* ---------------------------------------------------------- แก้ไข --- */}

      <Modal
        open={canEdit && editing !== null}
        onClose={closeEdit}
        title="แก้ไขข้อมูลผู้ขาย"
      >
        {editing === null ? null : (
          <form
            className="cap-form"
            data-testid="supplier-edit-form"
            aria-label="แก้ไขข้อมูลผู้ขาย"
            onSubmit={(event) => {
              event.preventDefault();
              void submitEdit();
            }}
          >
            {/**
             * รหัสผู้ขายเป็น **ข้อความ ไม่ใช่ช่องกรอกที่ disabled** โดยตั้งใจ:
             * ช่องที่กรอกไม่ได้ยังอ่านเหมือน "ช่องที่จะปลดล็อกได้สักวัน" ส่วนข้อความ
             * อ่านว่าเป็นข้อเท็จจริงของ record นี้ และไม่มี input ตัวไหนที่ค่าของมัน
             * จะหลุดไปกับคำขอได้เลย
             */}
            <div className="cap-field">
              <span className="cap-field__label">
                รหัสผู้ขาย{' '}
                <InfoHint
                  label="เกี่ยวกับการแก้รหัสผู้ขาย"
                  text={
                    <>
                      <strong>แก้ไม่ได้</strong> — เป็น business key ที่ใบสั่งซื้อ การตรวจรับ
                      และ lot อ้างอยู่ (D-083) คำขอนี้จึงไม่ส่งรหัสไปด้วยเลย
                    </>
                  }
                />
              </span>
              <p data-testid="supplier-edit-code">
                <code className="cap-table__code">{editing.supplier_code}</code>
              </p>
            </div>

            <label className="cap-field">
              <span className="cap-field__label">ชื่อผู้ขาย</span>
              <input
                className="cap-input"
                name="supplier_name"
                data-testid="supplier-edit-name"
                value={editName}
                maxLength={SUPPLIER_NAME_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setEditName(event.target.value);
                }}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">สถานะ</span>
              <select
                className="cap-input"
                name="status"
                data-testid="supplier-edit-status"
                value={editStatus}
                onChange={(event) => {
                  setEditStatus(event.target.value);
                }}
              >
                {offeredSupplierStatuses(editing.status).map((code) => (
                  <option key={code} value={code}>
                    {supplierStatusLabel(code)}
                  </option>
                ))}
              </select>
              <InfoHint
                label="เกี่ยวกับการเปลี่ยนสถานะผู้ขาย"
                text={
                  <>
                    เลิกใช้ผู้ขายรายนี้คือการตั้งสถานะเป็น INACTIVE —{' '}
                    <strong>ไม่มีการลบ</strong> เพราะเอกสารที่อ้างเขาอยู่ต้องตามสอบได้ต่อไป
                  </>
                }
              />
            </label>

            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="supplier-edit-submit"
                disabled={busy || editBody === null}
              >
                {pending === 'update-supplier' ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="supplier-edit-cancel"
                disabled={busy}
                onClick={closeEdit}
              >
                ยกเลิก
              </button>
            </div>

            {editFailure === null ? null : (
              <SupplierFailureNotice failure={editFailure} context="edit" />
            )}
          </form>
        )}
      </Modal>

      {updatedSupplier === null ? null : (
        <div className="cap-notice" data-testid="supplier-edit-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บันทึกการแก้ไขผู้ขาย {updatedSupplier.supplier_code} แล้ว
          </h2>
          <p className="cap-notice__text">
            ชื่อปัจจุบัน {updatedSupplier.supplier_name} สถานะ{' '}
            {supplierStatusLabel(updatedSupplier.status)} — ค่าเหล่านี้มาจากคำตอบของ
            เซิร์ฟเวอร์ ไม่ใช่จากสิ่งที่หน้าจอเพิ่งส่งไป
          </p>
        </div>
      )}

      {/* ------------------------------------------------------- รายการผู้ขาย -- */}

      {suppliers.status === 'loading' ? (
        <div className="cap-notice" data-testid="supplier-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการผู้ขาย…</p>
        </div>
      ) : null}

      {suppliers.status !== 'loading' && suppliers.status !== 'ready' ? (
        <SupplierFailureNotice
          failure={suppliers}
          context="suppliers"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {suppliers.status === 'ready' && supplierItems.length === 0 ? (
        <div className="cap-notice" data-testid="supplier-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีผู้ขาย</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีผู้ขายในระบบเลย</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มีผู้ขาย" และไม่ใช่ "โหลดไม่สำเร็จ"
       *
       * ทั้งสามอย่างให้หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่อคนละเรื่อง
       * กันโดยสิ้นเชิง — อันนี้แก้ด้วยการลบคำค้น ไม่ใช่การเพิ่มผู้ขายหรือโทรหาไอที
       */}
      {suppliers.status === 'ready' &&
      supplierItems.length > 0 &&
      matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="supplier-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบผู้ขายที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มีผู้ขายที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
          <p className="cap-notice__text">
            รายการนี้มีผู้ขาย {supplierItems.length} ราย แต่ไม่มีรายใดที่รหัสหรือชื่อมีคำนี้
            อยู่ — ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {suppliers.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="ผู้ขายทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">ผู้ขายทั้งหมด</h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามรหัสผู้ขายตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${supplierItems.length} รายที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="supplier-table">
              <caption className="cap-visually-hidden">
                ผู้ขายทั้งหมดขององค์กร พร้อมรหัสที่ใบสั่งซื้อและการตรวจรับอ้างถึง
              </caption>
              <thead>
                <tr>
                  <th scope="col">รหัสผู้ขาย</th>
                  <th scope="col">ชื่อผู้ขาย</th>
                  <th scope="col">สถานะ</th>
                  {canEdit ? <th scope="col">การแก้ไข</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((supplier) => (
                  <tr
                    key={supplier.supplier_id}
                    data-testid={`supplier-row-${supplier.supplier_code}`}
                  >
                    <th scope="row" className="cap-table__row-head">
                      <code className="cap-table__code">{supplier.supplier_code}</code>
                    </th>
                    <td>{supplier.supplier_name}</td>
                    <td>
                      <StatusBadge tone={supplierStatusTone(supplier.status)}>
                        {supplierStatusLabel(supplier.status)}
                      </StatusBadge>
                    </td>
                    {canEdit ? (
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`supplier-edit-open-${supplier.supplier_code}`}
                          onClick={() => {
                            openEdit(supplier);
                          }}
                        >
                          แก้ไข
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
